import {
  AccountType,
  Program,
  ProgramCategory,
  ProgressStep,
} from '@monorepo/types';

/**
 * Main function to calculate program progression chain for a given program ID
 * Ported from backend ProgramRulesService.getProgramProgression()
 */
export function calculateProgramProgression(
  allPrograms: Program[],
  programId: string,
): ProgressStep[] {
  // Filter enabled programs only
  const enabledPrograms = allPrograms.filter((program) => program.enabled);

  // Create adjacency map for efficient lookups
  const programMap = new Map<string, Program>();
  const nextProgramMap = new Map<string, string>();
  const incomingMap = new Map<string, string[]>();

  // Build maps
  for (const program of enabledPrograms) {
    programMap.set(program._id, program);

    // Get next program ID from nextProgramId or config.programEligibility
    const nextId = program.nextProgramId || program.config?.programEligibility;
    if (nextId) {
      nextProgramMap.set(program._id, nextId);

      // Track incoming connections
      if (!incomingMap.has(nextId)) {
        incomingMap.set(nextId, []);
      }
      const incomingList = incomingMap.get(nextId);
      if (incomingList) {
        incomingList.push(program._id);
      }
    }
  }

  // Find the entry point (root) of the chain containing the given programId
  const entryPoint = findChainEntryPoint(programId, programMap, incomingMap);

  if (!entryPoint) {
    return []; // Program not found or not connected to any chain
  }

  // Traverse the complete chain from entry point
  const progressionChain: Program[] = [];
  let currentId: string | undefined = entryPoint;
  const visited = new Set<string>();

  while (currentId && !visited.has(currentId)) {
    const currentProgram = programMap.get(currentId);
    if (!currentProgram) break;

    visited.add(currentId);
    progressionChain.push(currentProgram);

    // Move to next program
    currentId = nextProgramMap.get(currentId);
  }

  // Transform to ProgressStep format with numerical classification
  return buildProgressSteps(progressionChain, programId);
}

/**
 * Find the entry point (first program with sellable: true) of a chain
 */
function findChainEntryPoint(
  programId: string,
  programMap: Map<string, Program>,
  incomingMap: Map<string, string[]>,
): string | null {
  const startProgram = programMap.get(programId);
  if (!startProgram) return null;

  const targetGroupId = startProgram.programGroupId;
  const visited = new Set<string>();
  let currentId = programId;

  while (currentId && !visited.has(currentId)) {
    const currentProgram = programMap.get(currentId);

    if (!currentProgram) break;

    visited.add(currentId);

    if (currentProgram.sellable) {
      return currentId;
    }

    const incoming = incomingMap.get(currentId);
    if (!incoming || incoming.length === 0) {
      return currentId;
    }

    // When multiple chains converge on the same program, pick the best
    // incoming program using these priorities:
    //   1. Same programGroupId + Evaluation (the evaluation chain entry)
    //   2. Same programGroupId (any match)
    //   3. Evaluation type (prefer eval over funded scale-up chain)
    //   4. First in array (fallback)
    const scored = incoming.map((id) => {
      const p = programMap.get(id);
      const sameGroup = !!targetGroupId && p?.programGroupId === targetGroupId;
      const isEval = p?.accountType === AccountType.Evaluation;
      return { id, sameGroup, isEval };
    });

    const pick =
      scored.find((s) => s.sameGroup && s.isEval) ??
      scored.find((s) => s.sameGroup) ??
      scored.find((s) => s.isEval) ??
      scored[0];

    if (!pick) break;
    currentId = pick.id;
  }

  return null;
}

/**
 * Build progress steps with numerical classification and metadata
 */
function buildProgressSteps(
  progressionChain: Program[],
  activeProgramId: string,
): ProgressStep[] {
  // First pass: categorize programs by type
  const categories = categorizePrograms(progressionChain);

  // Second pass: build progress steps with all metadata
  let cumulativeTarget = 0;

  return progressionChain.map((program, index) => {
    const category = getProgramCategory(program, index, progressionChain);
    const categoryInfo = categories[category];
    const stepNumber = getStepNumberInCategory(
      program,
      index,
      progressionChain,
      category,
    );
    const target = program.config?.target || 0;
    const previousTarget =
      index > 0 ? progressionChain[index - 1].config?.target || 0 : undefined;

    cumulativeTarget += target;

    return {
      _id: program._id,
      name: program.name,
      buyingPower: program.config?.buyingPower || 0,
      target,

      // Numerical classification
      category,
      stepNumber,
      totalInCategory: categoryInfo.count,
      overallPosition: index + 1,
      totalPrograms: progressionChain.length,

      // Metadata
      isFirstInCategory: stepNumber === 1,
      isLastInCategory: stepNumber === categoryInfo.count,
      isFinalProgram: index === progressionChain.length - 1,

      // Progress calculation fields
      previousTarget,
      cumulativeTarget,

      // Active state based on the passed programId
      active: program._id === activeProgramId,
    };
  });
}

/**
 * Categorize all programs in the progression chain
 */
function categorizePrograms(
  progressionChain: Program[],
): Record<string, { count: number; indices: number[] }> {
  const categories = {
    [ProgramCategory.Evaluation]: { count: 0, indices: [] as number[] },
    [ProgramCategory.Funded]: { count: 0, indices: [] as number[] },
    [ProgramCategory.ScaleUp]: { count: 0, indices: [] as number[] },
  };

  progressionChain.forEach((program, index) => {
    const category = getProgramCategory(program, index, progressionChain);
    categories[category].count++;
    categories[category].indices.push(index);
  });

  return categories;
}

/**
 * Determine the category of a program
 */
function getProgramCategory(
  program: Program,
  index: number,
  progressionChain: Program[],
): ProgramCategory {
  // Evaluation accounts are straightforward
  if (program.accountType === AccountType.Evaluation) {
    return ProgramCategory.Evaluation;
  }

  // For funded accounts, check context
  if (program.accountType === AccountType.Funded) {
    // Check if there are any funded programs before this one
    const hasFundedBefore = progressionChain
      .slice(0, index)
      .some((p) => p.accountType === AccountType.Funded);

    if (!hasFundedBefore) {
      // This is the first funded account in the progression
      return ProgramCategory.Funded;
    } else {
      // This funded account comes after other funded accounts = scale-up
      return ProgramCategory.ScaleUp;
    }
  }

  // For any programs that come after a funded account, they are scale-ups
  const hasFundedBefore = progressionChain
    .slice(0, index)
    .some((p) => p.accountType === AccountType.Funded);

  if (hasFundedBefore) {
    return ProgramCategory.ScaleUp;
  }

  // Default fallback
  return ProgramCategory.Evaluation;
}

/**
 * Get the step number within a specific category
 */
function getStepNumberInCategory(
  program: Program,
  index: number,
  progressionChain: Program[],
  category: ProgramCategory,
): number {
  // Count how many programs of the same category come before this one
  let stepNumber = 1;

  for (let i = 0; i < index; i++) {
    const prevCategory = getProgramCategory(
      progressionChain[i],
      i,
      progressionChain,
    );
    if (prevCategory === category) {
      stepNumber++;
    }
  }

  return stepNumber;
}
